import { useEffect, useRef, useState } from "react";
import { useAuth } from "../context/AuthContext";
import { api } from "../lib/api";

const PUBLISHER_ID = process.env.REACT_APP_ADSENSE_PUBLISHER_ID || "";

// One env var per placement so slot IDs can land independently as Google
// approves them. Add a new placement here + drop `<AdSenseBanner
// placement="new-key" />` into the page you want to monetise. Unknown
// placement or missing env var → render nothing (no visible layout gap).
const SLOT_ENV_VARS = {
  "limit-banner": process.env.REACT_APP_ADSENSE_SLOT_LIMIT_BANNER
                 || process.env.REACT_APP_ADSENSE_AD_SLOT // legacy alias
                 || "",
  "discover-inline": process.env.REACT_APP_ADSENSE_SLOT_DISCOVER_INLINE || "",
  "library-inline": process.env.REACT_APP_ADSENSE_SLOT_LIBRARY_INLINE || "",
};

// The verification `<meta name="google-adsense-account">` tag lives in
// public/index.html; the ad-serving SDK is loaded on demand here so it
// only runs on pages that actually render an ad slot.
let _scriptLoaded = false;
function ensureAdsenseScript() {
  if (typeof document === "undefined" || _scriptLoaded || !PUBLISHER_ID) return;
  const s = document.createElement("script");
  s.async = true;
  s.crossOrigin = "anonymous";
  s.src = `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${PUBLISHER_ID}`;
  document.head.appendChild(s);
  _scriptLoaded = true;
}

/**
 * Google AdSense slot. Renders for signed-in FREE users who have hit their
 * monthly limit (books or recs). Never renders for anonymous visitors,
 * Reads+ subscribers, or free users still under the cap.
 *
 * Multi-placement: pass `placement="discover-inline"` etc. Slot IDs come
 * from `frontend/.env` — until the matching env var is set, this component
 * renders nothing (no visible placeholder), so the layout doesn't churn
 * when Google finally approves and the IDs land.
 */
export function AdSenseBanner({ placement = "limit-banner" }) {
  const { user } = useAuth();
  const [limits, setLimits] = useState(null);
  const insRef = useRef(null);

  const slotId = SLOT_ENV_VARS[placement] || "";

  useEffect(() => {
    let alive = true;
    // Only signed-in users see (or don't see) ads.
    if (!user || typeof user !== "object") return;
    const load = () => api.get("/user/me/limits").then(({ data }) => { if (alive) setLimits(data); }).catch(() => {});
    load();
    // Re-fetch when the app fires the "you just hit your monthly cap" event
    // so ads appear immediately, not on the next page refresh.
    const onLimit = () => load();
    window.addEventListener("freemium:monthly-limit", onLimit);
    return () => { alive = false; window.removeEventListener("freemium:monthly-limit", onLimit); };
  }, [user]);

  const shouldRender = Boolean(limits && !limits.is_plus && limits.at_limit);

  useEffect(() => {
    if (!shouldRender || !PUBLISHER_ID || !slotId) return;
    ensureAdsenseScript();
    try {
      // eslint-disable-next-line no-undef
      (window.adsbygoogle = window.adsbygoogle || []).push({});
    } catch { /* SDK not ready yet — will retry when component next mounts */ }
  }, [shouldRender, slotId]);

  // No slot ID configured for this placement → render nothing. This lets us
  // ship the slots ahead of Google approval without a visible dev-only
  // placeholder polluting production.
  if (!shouldRender || !PUBLISHER_ID || !slotId) return null;

  return (
    <div className="w-full my-4" data-testid={`adsense-${placement}`}>
      <ins
        ref={insRef}
        className="adsbygoogle"
        style={{ display: "block" }}
        data-ad-client={PUBLISHER_ID}
        data-ad-slot={slotId}
        data-ad-format="auto"
        data-full-width-responsive="true"
      />
    </div>
  );
}
